<script setup lang="ts">
    import { watch, ref, onUnmounted, computed } from 'vue'
    import type { LocalizedString } from 'models'
    import { useSafeLocalStorage, useLocalizedString } from 'composables'

    const props = withDefaults(
        defineProps<{
            agentId: string
            message?: LocalizedString
            enabled?: boolean
            delay?: number
        }>(),
        {
            message: undefined,
            delay: 1500,
        },
    )

    const emit = defineEmits<{
        open: []
    }>()

    const resolvedMessage = useLocalizedString(
        () => props.message,
        'message.defaultAgentWelcome',
    )

    const isDismissed = useSafeLocalStorage<boolean>(
        computed(() => `${props.agentId}-welcome-popup-dismissed`),
        false,
    )
    const isVisible = ref(false)
    const isStreamingDone = ref(false)
    const streamedWords = ref<string[]>([])
    let streamTimer: ReturnType<typeof setTimeout> | undefined
    let delayTimer: ReturnType<typeof setTimeout> | undefined

    const WORD_INTERVAL_MS = 100

    function startStreamingText(text: string) {
        streamedWords.value = []
        isStreamingDone.value = false
        const words = text.split(/(\s+)/)
        let i = 0
        function tick() {
            if (i < words.length) {
                streamedWords.value.push(words[i])
                i++
                streamTimer = setTimeout(tick, WORD_INTERVAL_MS)
            } else {
                isStreamingDone.value = true
            }
        }
        tick()
    }

    function clearTimers() {
        if (streamTimer !== undefined) {
            clearTimeout(streamTimer)
            streamTimer = undefined
        }
        if (delayTimer !== undefined) {
            clearTimeout(delayTimer)
            delayTimer = undefined
        }
        streamedWords.value = []
        isStreamingDone.value = false
    }

    function dismiss() {
        isVisible.value = false
        isDismissed.value = true
        clearTimers()
    }

    function onOpen() {
        dismiss()
        emit('open')
    }

    watch(
        () => props.enabled,
        (enabled) => {
            if (enabled && !isDismissed.value) {
                delayTimer = setTimeout(() => {
                    if (props.enabled && !isDismissed.value) {
                        isVisible.value = true
                        startStreamingText(resolvedMessage.value ?? '')
                    }
                }, props.delay)
            }
            if (!enabled) {
                isVisible.value = false
                clearTimers()
            }
        },
        { immediate: true },
    )

    onUnmounted(() => {
        clearTimers()
    })
</script>

<template>
    <Transition name="welcome-popup">
        <div v-if="isVisible" class="welcome-popup" @click.stop="onOpen">
            <p class="welcome-popup__text">
                <span
                    v-for="(word, i) in streamedWords"
                    :key="i"
                    class="welcome-popup__word"
                    >{{ word }}</span
                >
            </p>
            <Transition>
                <button
                    v-if="isStreamingDone"
                    type="button"
                    class="welcome-popup__close"
                    :aria-label="$t('action.close')"
                    @click.stop="dismiss">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        width="14"
                        height="14"
                        fill="currentColor">
                        <path
                            d="M12.0007 10.5865L16.9504 5.63672L18.3646 7.05093L13.4149 12.0007L18.3646 16.9504L16.9504 18.3646L12.0007 13.4149L7.05093 18.3646L5.63672 16.9504L10.5865 12.0007L5.63672 7.05093L7.05093 5.63672L12.0007 10.5865Z" />
                    </svg>
                </button>
            </Transition>
        </div>
    </Transition>
</template>

<style lang="scss">
    .welcome-popup {
        max-width: 300px;
        background: var(--color-surface);
        pointer-events: auto;
        color: var(--color-word-2);
        line-height: var(--leading-normal);
        border-width: var(--spacing-px);
        border-color: var(--color-surface-3);
        padding: var(--spacing-sm);
        border-radius: var(--rounded-xl);
        font-size: var(--text-14);
        cursor: pointer;
        box-shadow:
            0px 4px 16px 0px rgb(0 0 0 / 8%),
            0px 1px 4px 0px rgb(0 0 0 / 6%);

        &__word {
            display: inline;
            animation: welcome-popup-word-in 0.3s ease-out both;
        }

        &__close {
            background-color: var(--color-surface);
            border: 1px solid var(--color-surface-3);
            border-radius: var(--rounded-full);
            width: var(--spacing-20);
            height: var(--spacing-20);
            position: absolute;
            top: calc(-1 * var(--spacing-8));
            right: calc(-1 * var(--spacing-8));
            padding: var(--spacing-2);
            cursor: pointer;
            color: var(--color-word-4);
            line-height: 1;
            border-radius: var(--rounded-full);
            transition: var(--transition-colors);
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: var(--shadow-md);

            &:hover {
                color: var(--color-word);
            }
        }
    }

    .welcome-popup-enter-active,
    .welcome-popup-leave-active {
        transition:
            transform var(--duration-300) var(--ease-out),
            opacity var(--duration-300) var(--ease-out);
    }

    .welcome-popup-enter-from {
        opacity: 0;
        transform: translateY(var(--spacing-32));
    }

    .welcome-popup-leave-to {
        opacity: 0;
    }

    @keyframes welcome-popup-word-in {
        from {
            opacity: 0;
            transform: translateY(4px);
        }

        to {
            opacity: 1;
            transform: translateY(0);
        }
    }
</style>
